import { getRequest, postRequest, putRequest, patchRequest, deleteRequest } from './api';

const USAGE = '/api/ai-command/usage';

export const getUsageSummary = (params = {}) =>
  getRequest('manager', `${USAGE}/summary`, params);

export const getUsageUsers = (params = {}) =>
  getRequest('manager', `${USAGE}/users`, params);

export const getUsageTrends = (params = {}) =>
  getRequest('manager', `${USAGE}/trends`, params);

export const getAdoptionCurve = (params = {}) =>
  getRequest('manager', `${USAGE}/adoption-curve`, params);

export const getWeeklyAdoption = (params = {}) =>
  getRequest('manager', `${USAGE}/weekly-adoption`, params);

const COSTS = '/api/ai-command/costs';

export const getCostOverview = (params = {}) =>
  getRequest('manager', `${COSTS}/overview`, params);

export const getCostBreakdown = (params = {}) =>
  getRequest('manager', `${COSTS}/breakdown`, params);

export const getCostProjection = (params = {}) =>
  getRequest('manager', `${COSTS}/projection`, params);

const PROD = '/api/ai-command/productivity';

export const getProductivityScores = (params = {}) =>
  getRequest('manager', `${PROD}/scores`, params);

export const getProductivityCorrelation = (params = {}) =>
  getRequest('manager', `${PROD}/correlation`, params);

export const getProductivityToolComparison = (period = '30d') =>
  getRequest('manager', `${PROD}/tool-comparison`, { period });

export const getProductivityCompare = (teams, period = '30d', tier = '1') =>
  getRequest('manager', `${PROD}/compare`, { teams: teams.join(','), period, tier }, { timeout: 130000 });

export const analyzeProductivityCompare = (teams, metrics) =>
  postRequest('manager', `${PROD}/compare/analyze`, { teams, metrics });

const GOV = '/api/ai-command/governance';

export const getBudgets = (params = {}) =>
  getRequest('manager', `${GOV}/budgets`, params);

export const createBudget = (data) =>
  postRequest('manager', `${GOV}/budgets`, data);

export const updateBudget = (id, data) =>
  putRequest('manager', `${GOV}/budgets/${id}`, data);

export const deleteBudget = (id) =>
  deleteRequest('manager', `${GOV}/budgets/${id}`);

export const getAlerts = (params = {}) =>
  getRequest('manager', `${GOV}/alerts`, params);

export const acknowledgeAlert = (id) =>
  postRequest('manager', `${GOV}/alerts/${id}/acknowledge`);

export const getWasteScores = (params = {}) =>
  getRequest('manager', `${GOV}/waste-scores`, params);

export const getUsageEfficiency = (params = {}) =>
  getRequest('manager', `${GOV}/usage-efficiency`, params);

export const getEnforcementSettings = () =>
  getRequest('manager', `${GOV}/enforcement`);

export const updateEnforcementSettings = (data) =>
  putRequest('manager', `${GOV}/enforcement`, data);

const EST = '/api/ai-command/estimation';

export const getEstimationBenchmarks = () =>
  getRequest('manager', `${EST}/benchmarks`);

export const estimateProjectCost = (data) =>
  postRequest('manager', `${EST}/estimate`, data);

export const compareModels = (data) =>
  postRequest('manager', `${EST}/compare-models`, data);

export const getRoiProjection = (params = {}) =>
  getRequest('manager', `${EST}/roi-projection`, params);

export const getTeamMetrics = (params = {}) =>
  getRequest('manager', `${EST}/team-metrics`, params);

export const getTeamComparison = (params = {}) =>
  getRequest('manager', `${EST}/team-comparison`, params);

export const getEstimationTeams = () =>
  getRequest('manager', `${EST}/teams`);

export const getTeamRepos = (team) =>
  getRequest('manager', `${EST}/team/${encodeURIComponent(team)}/repos`);

export const analyzeTeam = (team, params = {}) =>
  postRequest('manager', `${EST}/team/${encodeURIComponent(team)}/analyze`, null, params);

export const getOrgBenchmark = (params = {}) =>
  getRequest('manager', `${EST}/org-benchmark`, params);

export const estimateV2 = (data) =>
  postRequest('manager', `${EST}/v2/estimate`, data, { signal: AbortSignal.timeout(60000) });

const HIERARCHY = '/api/ai-command/hierarchy';

export const getHierarchyData = (params = {}) =>
  getRequest('manager', `${HIERARCHY}/data`, params, { timeout: 130000 });

export const getHierarchyPrefetch = (params = {}) =>
  getRequest('manager', `${HIERARCHY}/prefetch`, params, { timeout: 130000 });

export const searchHierarchy = (q, options = {}) => {
  const { peopleOnly, ...requestOptions } = options;
  const params = { q };
  if (peopleOnly) params.people_only = true;
  return getRequest('manager', `${HIERARCHY}/search`, params, requestOptions);
};

const REPORTS = '/api/ai-command/reports';

export const requestExport = (data) =>
  postRequest('manager', `${REPORTS}/export`, data);

export const getExportStatus = (jobId) =>
  getRequest('manager', `${REPORTS}/export/${jobId}/status`);

export const downloadExport = async (jobId) => {
  const token = localStorage.getItem('authToken');
  const url = `/api/manager${REPORTS}/export/${jobId}/download`;
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    if (response.status === 401) {
      const { default: useAuthStore } = await import('../stores/auth-store');
      useAuthStore.getState().clearAuth();
    }
    const err = await response.text().catch(() => '');
    throw new Error(err || `Download failed (${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `export-${jobId.slice(0, 8)}`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
};

export const listExports = (params = {}) =>
  getRequest('manager', `${REPORTS}/exports`, params);

export const createSchedule = (data) =>
  postRequest('manager', `${REPORTS}/schedules`, data);

export const listSchedules = () =>
  getRequest('manager', `${REPORTS}/schedules`);

export const updateSchedule = (id, data) =>
  putRequest('manager', `${REPORTS}/schedules/${id}`, data);

export const deleteSchedule = (id) =>
  deleteRequest('manager', `${REPORTS}/schedules/${id}`);

const NOTIF = '/api/ai-command/notifications';

export const getNotifications = (params = {}) =>
  getRequest('manager', `${NOTIF}/notifications`, params);

export const getNotificationCount = () =>
  getRequest('manager', `${NOTIF}/notifications/count`);

export const markNotificationRead = (id) =>
  postRequest('manager', `${NOTIF}/notifications/${id}/read`);

export const markAllNotificationsRead = () =>
  postRequest('manager', `${NOTIF}/notifications/read-all`);

export const getApprovals = (params = {}) =>
  getRequest('manager', `${NOTIF}/approvals`, params);

export const createApproval = (data) =>
  postRequest('manager', `${NOTIF}/approvals`, data);

export const reviewApproval = (id, data) =>
  postRequest('manager', `${NOTIF}/approvals/${id}/review`, data);

const DASH = '/api/ai-command/dashboards';

export const getDashboards = () =>
  getRequest('manager', DASH);

export const createDashboard = (data) =>
  postRequest('manager', DASH, data);

export const getDashboard = (id) =>
  getRequest('manager', `${DASH}/${id}`);

export const updateDashboard = (id, data) =>
  putRequest('manager', `${DASH}/${id}`, data);

export const deleteDashboard = (id) =>
  deleteRequest('manager', `${DASH}/${id}`);

export const addDashboardItem = (dashId, data) =>
  postRequest('manager', `${DASH}/${dashId}/items`, data);

export const updateDashboardItem = (dashId, itemId, data) =>
  putRequest('manager', `${DASH}/${dashId}/items/${itemId}`, data);

export const deleteDashboardItem = (dashId, itemId) =>
  deleteRequest('manager', `${DASH}/${dashId}/items/${itemId}`);

export const saveDashboardLayout = (dashId, items) =>
  putRequest('manager', `${DASH}/${dashId}/layout`, { items });

export const executeDashboardWidget = (dashId, itemId) =>
  postRequest('manager', `${DASH}/${dashId}/items/${itemId}/execute`);

export const createWebSocket = (token) => {
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const host = process.env.REACT_APP_MANAGER_URL?.replace(/^https?:\/\//, '') || 'localhost:8013';
  return new WebSocket(`${protocol}://${host}/api/ai-command/ws?token=${token}`);
};

const TRANSFORM = '/api/ai-command/transformation';

export const getTransformations = (params = {}) =>
  getRequest('manager', TRANSFORM, params);

export const getTeamTransformation = (team) =>
  getRequest('manager', `${TRANSFORM}/${encodeURIComponent(team)}`);

const ROI = '/api/ai-command/roi';

export const getTeamROI = (team) =>
  getRequest('manager', `${ROI}/team/${encodeURIComponent(team)}`);

export const getTeamROITrend = (team, params = {}) =>
  getRequest('manager', `${ROI}/team/${encodeURIComponent(team)}/trend`, params);

export const getROISummary = () =>
  getRequest('manager', `${ROI}/summary`);

export const getMyEfficiency = () =>
  getRequest('manager', `${ROI}/me/efficiency`);

export const getUserEfficiency = (email) =>
  getRequest('manager', `${ROI}/user/${encodeURIComponent(email)}/efficiency`);

export const getUserEfficiencyTrend = (email, params = {}) =>
  getRequest('manager', `${ROI}/user/${encodeURIComponent(email)}/trend`, params);

export const getTeamEfficiency = (team) =>
  getRequest('manager', `${ROI}/team/${encodeURIComponent(team)}/efficiency`);

const RECS = '/api/ai-command/recommendations';

export const getRecommendations = (params = {}) =>
  getRequest('manager', RECS, params);

export const getRecommendationStats = () =>
  getRequest('manager', `${RECS}/summary/stats`);

export const updateRecommendationStatus = (id, status) =>
  patchRequest('manager', `${RECS}/${id}/status?status=${encodeURIComponent(status)}`, {});

export const submitRecommendationFeedback = (id, data) =>
  postRequest('manager', `${RECS}/${id}/feedback`, data);

export const estimateDualCost = (data) =>
  postRequest('manager', `${EST}/dual-cost`, data);

export const getCostByProduct = (params = {}) =>
  getRequest('manager', `${COSTS}/breakdown`, { ...params, group_by: 'product' });

export const getCostByModel = (params = {}) =>
  getRequest('manager', `${COSTS}/breakdown`, { ...params, group_by: 'model' });

export const getTokenBreakdown = (params = {}) =>
  getRequest('manager', `${COSTS}/token-breakdown`, params);

export const getCacheEfficiency = (params = {}) =>
  getRequest('manager', `${COSTS}/cache-efficiency`, params);

export const getConsumptionSummary = (params = {}) =>
  getRequest('manager', `${COSTS}/consumption-summary`, params);

export const getActivitySummary = (params = {}) =>
  getRequest('manager', '/api/ai-command/activity/summary', params);

export const getUserDashboard = (email, period = '30d') =>
  getRequest('manager', `${HIERARCHY}/user-dashboard`, { email, period });

export const getUserDashboardAll = (email, period = '30d') =>
  getRequest('manager', `${HIERARCHY}/user-dashboard/all`, { email, period });

export const getUserCostByProduct = (params = {}) =>
  getRequest('manager', `${HIERARCHY}/user-dashboard-breakdown`, {
    email: params.email, period: params.period, group_by: 'product',
  });

export const getUserCostByModel = (params = {}) =>
  getRequest('manager', `${HIERARCHY}/user-dashboard-breakdown`, {
    email: params.email, period: params.period, group_by: 'model',
  });

export const getUserCostByCostType = (params = {}) =>
  getRequest('manager', `${HIERARCHY}/user-dashboard-breakdown`, {
    email: params.email, period: params.period, group_by: 'cost_type',
  });

export const getUserTokenBreakdown = (params = {}) =>
  getRequest('manager', `${HIERARCHY}/user-token-breakdown`, {
    email: params.email, period: params.period,
  });

export const getUserDailySpendByModel = (params = {}) =>
  getRequest('manager', `${HIERARCHY}/user-dashboard-breakdown`, {
    email: params.email, period: params.period, group_by: 'daily_model',
  });

export const getEnterpriseBreakdown = (params = {}) =>
  getRequest('manager', `${HIERARCHY}/enterprise-breakdown`, {
    period: params.period, tier: params.tier, parent: params.parent,
  }, { timeout: 130000 });

export const getEnterprisePortfolios = () =>
  getRequest('manager', '/api/ai-command/enterprise/portfolios', {});

export const getEnterpriseKpis = (params = {}) =>
  getRequest('manager', '/api/ai-command/enterprise/kpis', {
    tier: params.tier,
    period: params.period,
    parent: params.parent,
    portfolio_manager_id: params.portfolio_manager_id,
  });

export const getEnterpriseDailySpend = (params = {}) =>
  getRequest('manager', '/api/ai-command/enterprise/daily-spend', {
    tier: params.tier,
    period: params.period,
    parent: params.parent,
    portfolio_manager_id: params.portfolio_manager_id,
  });

export const getEnterpriseSpendBreakdown = (params = {}) =>
  getRequest('manager', '/api/ai-command/enterprise/spend-breakdown', {
    tier: params.tier,
    period: params.period,
    parent: params.parent,
    portfolio_manager_id: params.portfolio_manager_id,
  });

export const getEnterpriseTokenEconomy = (params = {}) =>
  getRequest('manager', '/api/ai-command/enterprise/token-economy', {
    tier: params.tier,
    period: params.period,
    parent: params.parent,
    portfolio_manager_id: params.portfolio_manager_id,
  });

export const getEnterpriseActivity = (params = {}) =>
  getRequest('manager', '/api/ai-command/enterprise/activity', {
    tier: params.tier,
    period: params.period,
    parent: params.parent,
    portfolio_manager_id: params.portfolio_manager_id,
  });

export const getEnterpriseAdoption = (params = {}) =>
  getRequest('manager', '/api/ai-command/enterprise/adoption', {
    tier: params.tier,
    period: params.period,
    parent: params.parent,
    portfolio_manager_id: params.portfolio_manager_id,
  });

export const getDrAdoption = (period = '30d', managerEmail) =>
  getRequest('manager', '/api/ai-command/enterprise/dr-adoption', { period, manager_email: managerEmail });

export const getEnterpriseAll = (params = {}) =>
  getRequest('manager', '/api/ai-command/enterprise/all', {
    tier: params.tier,
    period: params.period,
    parent: params.parent,
    portfolio_manager_id: params.portfolio_manager_id,
  });

export const getDirectReportsData = (period = '30d', managerEmail) =>
  getRequest('manager', '/api/ai-command/hierarchy/data', {
    query_type: 'enterprise', tier: 5, period, manager_email: managerEmail,
  });

export const getDirectReportsBreakdown = (breakdownType, period = '30d', managerEmail) =>
  getRequest('manager', '/api/ai-command/hierarchy/breakdown', {
    breakdown_type: breakdownType, period, manager_email: managerEmail,
  });

const MULTI_VENDOR = '/api/ai-command/multi-vendor';

export const getVendorComparison = (params = {}) =>
  getRequest('manager', `${MULTI_VENDOR}/comparison`, params);

export const getCopilotSummary = (params = {}) =>
  getRequest('manager', `${MULTI_VENDOR}/copilot/summary`, params);

export const getMsCopilotSummary = (params = {}) =>
  getRequest('manager', `${MULTI_VENDOR}/ms-copilot/summary`, params);

export const getMsCopilotTrends = (params = {}) =>
  getRequest('manager', `${MULTI_VENDOR}/ms-copilot/trends`, params);

const MATURITY = '/api/ai-command/maturity';

export const getMaturityScores = (params = {}) => {
  const { signal, ...queryParams } = params;
  return getRequest('manager', `${MATURITY}/scores`, queryParams, { signal });
};

export const getMaturityOrgSummary = (params = {}) =>
  getRequest('manager', `${MATURITY}/org-summary`, params);

const OUTCOMES = '/api/ai-command/outcomes';

export const getMyOutcomes = ({ signal, ...query } = {}) =>
  getRequest('manager', `${OUTCOMES}/my`, query, { signal });

export const getEnterpriseOutcomes = ({ signal, ...query } = {}) =>
  getRequest('manager', `${OUTCOMES}/enterprise`, query, { signal });

export const getEnterpriseRollup = ({ signal, period, level, team_name, parent } = {}) =>
  getRequest('manager', `${OUTCOMES}/enterprise-rollup`, { period, level, team_name, parent }, { signal });

export const getOrgOutcomes = ({ signal, ...query } = {}) =>
  getRequest('manager', `${OUTCOMES}/org`, query, { signal });

export const getCascadedOutcomes = ({ signal, ...query } = {}) =>
  getRequest('manager', `${OUTCOMES}/cascaded`, query, { signal });

export const getOutcomeBadgeCounts = () =>
  getRequest('manager', `${OUTCOMES}/badge-counts`);

export const createOutcome = (data) =>
  postRequest('manager', OUTCOMES, data);

export const getOutcome = (id) =>
  getRequest('manager', `${OUTCOMES}/${id}`);

export const updateOutcome = (id, data) =>
  putRequest('manager', `${OUTCOMES}/${id}`, data);

export const deleteOutcome = (id) =>
  deleteRequest('manager', `${OUTCOMES}/${id}`);

export const createCheckin = (outcomeId, data) =>
  postRequest('manager', `${OUTCOMES}/${outcomeId}/checkin`, data);

export const getCheckins = (outcomeId) =>
  getRequest('manager', `${OUTCOMES}/${outcomeId}/checkins`);

export const getOutcomeTemplates = () =>
  getRequest('manager', `${OUTCOMES}/templates`);

export const getOutcomeRollup = ({ signal, ...query } = {}) =>
  getRequest('manager', `${OUTCOMES}/rollup`, query, { signal });

export const approveOutcome = (id) =>
  postRequest('manager', `${OUTCOMES}/${id}/approve`);

export const kickbackOutcome = (id, reason) =>
  postRequest('manager', `${OUTCOMES}/${id}/kickback`, { reason });

export const resubmitOutcome = (id) =>
  postRequest('manager', `${OUTCOMES}/${id}/resubmit`);

export const getLatestCheckin = (outcomeId) =>
  getRequest('manager', `${OUTCOMES}/${outcomeId}/checkin-latest`);

export const verifyCheckin = (outcomeId, checkinId, note = '') =>
  postRequest('manager', `${OUTCOMES}/${outcomeId}/checkins/${checkinId}/verify`, { note });

export const sendbackCheckin = (outcomeId, checkinId, note) =>
  postRequest('manager', `${OUTCOMES}/${outcomeId}/checkins/${checkinId}/sendback`, { note });

export const requestInfoCheckin = (outcomeId, checkinId, note) =>
  postRequest('manager', `${OUTCOMES}/${outcomeId}/checkins/${checkinId}/request-info`, { note });

export const getEntitlementForUser = (email, asOfDate) =>
  getRequest(
    'manager',
    '/api/ai-command/personal-entitlement/entitlement',
    {
      user_email: (email ?? '').trim().toLowerCase(),
      ...(asOfDate ? { as_of_date: asOfDate } : {}),
    },
  );

export const getUserMonthlyTrend = (email, monthsBack = 6) => {
  const normalized = (email ?? '').trim().toLowerCase();
  return getRequest('manager', '/api/ai-command/hierarchy/user-dashboard/monthly-trend', {
    email: normalized,
    months_back: monthsBack,
  });
};
